const CMP_IFRAME_TAG = 'IFRAME';
const CMP_SOCIAL_CLASS = 'optanon-category-5';
const CMP_FUNCTIONAL_CLASS = 'optanon-category-3';
const CMP_CONSENT_READY_EVENT = 'ParamountCMP.1';
const CMP_NETWORK_JURISDICTION_EVENT = 'ParamountCMP.Network.4';
const EMBED_BLOCKED_MESSAGE_CLASS = 'cmp-embed-blocked-message';
const PLAYLIST_EMBED_SELECTOR = '#embed-iframe, iframe[data-testid="embed-iframe"]';

let cmpJurisdictionCode = null;
let socialAllowedState = null;
let functionalAllowedState = null;
let lastConsentStateSnapshot = null;

const decodeStorageValue = (value) => {
  if (!value) {
    return null;
  }

  try {
    return JSON.parse(value);
  } catch (error) {
    // Keep trying alternate encodings below.
  }

  try {
    const normalized = value.replace(/-/g, '+').replace(/_/g, '/');
    const padding = '='.repeat((4 - (normalized.length % 4)) % 4);
    return JSON.parse(window.atob(normalized + padding));
  } catch (error) {
    return null;
  }
};

const getConsentStateSnapshot = () => {
  const consent = window.localStorage.getItem('_ketch_consent_v1_') || '';
  const metadata = window.localStorage.getItem('_swb_consent__metadata') || '';
  const swbConsent = window.localStorage.getItem('_swb_consent_') || '';
  return `${consent}::${metadata}::${swbConsent}`;
};

const isSocialConsentGranted = () => {
  const storedConsent = decodeStorageValue(window.localStorage.getItem('_ketch_consent_v1_'));
  return storedConsent?.social_media?.status === 'granted';
};

const isSocialConsentDenied = () => {
  const storedConsent = decodeStorageValue(window.localStorage.getItem('_ketch_consent_v1_'));
  return storedConsent?.social_media?.status === 'denied';
};

const isFunctionalConsentGranted = () => {
  const storedConsent = decodeStorageValue(window.localStorage.getItem('_ketch_consent_v1_'));
  return storedConsent?.functional?.status === 'granted';
};

const isFunctionalConsentDenied = () => {
  const storedConsent = decodeStorageValue(window.localStorage.getItem('_ketch_consent_v1_'));
  return storedConsent?.functional?.status === 'denied';
};

const setCmpJurisdiction = (value) => {
  if (typeof value === 'string' && value.length > 0) {
    cmpJurisdictionCode = value;
  }
};

const primeCmpJurisdictionFromStorage = () => {
  if (cmpJurisdictionCode) {
    return;
  }

  const storedConsent = decodeStorageValue(window.localStorage.getItem('_swb_consent_'));
  setCmpJurisdiction(storedConsent?.jurisdictionCode || null);
};

const registerCmpJurisdictionEvents = () => {
  window.addEventListener(CMP_CONSENT_READY_EVENT, (event) => {
    setCmpJurisdiction(event?.detail?.consentModel || event?.detail?.jurisdictionCode || null);
  });

  window.addEventListener(CMP_NETWORK_JURISDICTION_EVENT, (event) => {
    setCmpJurisdiction(event?.detail?.jurisdictionCode || event?.detail?.code || null);
  });
};

const getJurisdictionCode = () => {
  if (cmpJurisdictionCode) {
    return cmpJurisdictionCode;
  }

  primeCmpJurisdictionFromStorage();
  return cmpJurisdictionCode;
};

const shouldAllowSocialEmbedsByRegion = () => {
  if (socialAllowedState === true) {
    return true;
  }

  if (isSocialConsentDenied()) {
    return false;
  }

  if (isSocialConsentGranted()) {
    return true;
  }

  const jurisdictionCode = getJurisdictionCode();
  if (typeof jurisdictionCode === 'string' && jurisdictionCode.startsWith('us_')) {
    // US consent models are typically opt-out; allow unless explicitly denied.
    return true;
  }

  return false;
};

const shouldAllowFunctionalEmbedsByRegion = () => {
  if (functionalAllowedState === true) {
    return true;
  }

  if (isFunctionalConsentDenied()) {
    return false;
  }

  if (isFunctionalConsentGranted()) {
    return true;
  }

  const jurisdictionCode = getJurisdictionCode();
  if (typeof jurisdictionCode === 'string' && jurisdictionCode.startsWith('us_')) {
    return true;
  }

  return false;
};

const isUsJurisdiction = () => {
  const jurisdictionCode = getJurisdictionCode();
  return typeof jurisdictionCode === 'string' && jurisdictionCode.startsWith('us_');
};

const resolveFunctionalAllowed = (cmpAllowed) => {
  if (cmpAllowed === true) {
    return true;
  }

  if (cmpAllowed === false) {
    if (isUsJurisdiction() && !isFunctionalConsentDenied()) {
      return true;
    }

    return false;
  }

  return shouldAllowFunctionalEmbedsByRegion();
};

const resolveSocialAllowed = (cmpAllowed) => {
  if (cmpAllowed === true) {
    return true;
  }

  if (cmpAllowed === false) {
    if (isUsJurisdiction() && !isSocialConsentDenied()) {
      return true;
    }

    return false;
  }

  return shouldAllowSocialEmbedsByRegion();
};

const getBlockedMessage = (className) => {
  if (className === CMP_FUNCTIONAL_CLASS) {
    return 'Please enable Functional Cookies to view this content.';
  }

  return 'Please enable Social Media Cookies to view this video.';
};

const getEmbedFallbackUrl = (iframe) => {
  const explicitFallback = iframe.getAttribute('data-cmp-fallback-url');
  if (explicitFallback) {
    return explicitFallback;
  }

  const deferredSrc = iframe.getAttribute('data-src');
  if (!deferredSrc) {
    return null;
  }

  try {
    const parsed = new URL(deferredSrc, window.location.origin);

    // Convert Spotify embed URLs to direct listen URLs.
    if (parsed.hostname.includes('spotify.com') && parsed.pathname.includes('/embed/')) {
      parsed.pathname = parsed.pathname.replace('/embed/', '/');
      return parsed.toString();
    }

    return parsed.toString();
  } catch (error) {
    return deferredSrc;
  }
};

const getEmbedFallbackLabel = (fallbackUrl) => {
  if (!fallbackUrl) {
    return 'Open this content in a new tab';
  }

  try {
    const parsed = new URL(fallbackUrl, window.location.origin);
    if (parsed.hostname.includes('spotify.com')) {
      return 'Listen on Spotify';
    }
    if (parsed.hostname.includes('youtube.com') || parsed.hostname.includes('youtu.be')) {
      return 'Watch this video on YouTube';
    }
  } catch (error) {
    // Fall through to generic label.
  }

  return 'Open this content in a new tab';
};

const getOrCreateBlockedMessageNode = (iframe, className) => {
  const next = iframe.nextElementSibling;
  if (next && next.classList.contains(EMBED_BLOCKED_MESSAGE_CLASS)) {
    const copyNode = next.querySelector('.cmp-embed-blocked-copy');
    if (copyNode) {
      copyNode.textContent = getBlockedMessage(className);
    }

    const fallbackUrl = getEmbedFallbackUrl(iframe);
    const linkNode = next.querySelector('.cmp-embed-blocked-link');
    const orNode = next.querySelector('.cmp-embed-blocked-or');
    if (linkNode && fallbackUrl) {
      linkNode.setAttribute('href', fallbackUrl);
      linkNode.textContent = getEmbedFallbackLabel(fallbackUrl);
      if (!orNode) {
        const newOr = document.createElement('span');
        newOr.className = 'cmp-embed-blocked-or';
        newOr.textContent = 'OR';
        linkNode.insertAdjacentElement('beforebegin', newOr);
      } else {
        orNode.style.display = '';
      }
    }

    if (linkNode && !fallbackUrl) {
      linkNode.remove();
      if (orNode) {
        orNode.remove();
      }
    }

    if (!linkNode && fallbackUrl) {
      const newOr = document.createElement('span');
      newOr.className = 'cmp-embed-blocked-or';
      newOr.textContent = 'OR';
      next.appendChild(newOr);

      const newLink = document.createElement('a');
      newLink.className = 'cmp-embed-blocked-link';
      newLink.textContent = getEmbedFallbackLabel(fallbackUrl);
      newLink.target = '_blank';
      newLink.rel = 'noopener noreferrer';
      newLink.setAttribute('href', fallbackUrl);
      next.appendChild(newLink);
    }

    return next;
  }

  const messageNode = document.createElement('div');
  messageNode.className = EMBED_BLOCKED_MESSAGE_CLASS;
  messageNode.setAttribute('role', 'note');

  const copyNode = document.createElement('p');
  copyNode.className = 'cmp-embed-blocked-copy';
  copyNode.textContent = getBlockedMessage(className);
  messageNode.appendChild(copyNode);

  const fallbackUrl = getEmbedFallbackUrl(iframe);
  if (fallbackUrl) {
    const orNode = document.createElement('span');
    orNode.className = 'cmp-embed-blocked-or';
    orNode.textContent = 'OR';
    messageNode.appendChild(orNode);

    const linkNode = document.createElement('a');
    linkNode.className = 'cmp-embed-blocked-link';
    linkNode.textContent = getEmbedFallbackLabel(fallbackUrl);
    linkNode.target = '_blank';
    linkNode.rel = 'noopener noreferrer';
    linkNode.setAttribute('href', fallbackUrl);
    messageNode.appendChild(linkNode);
  }

  iframe.insertAdjacentElement('afterend', messageNode);
  return messageNode;
};

const setIframeBlockedState = (iframe, className, blocked) => {
  const messageNode = getOrCreateBlockedMessageNode(iframe, className);

  if (blocked) {
    iframe.removeAttribute('src');
    iframe.dataset.cmpNeedsReload = 'true';
    iframe.style.display = 'none';
    messageNode.style.display = 'flex';
    return;
  }

  iframe.style.display = '';
  messageNode.style.display = 'none';
};

const updateBlockedStateForClass = (className, allowed) => {
  document.querySelectorAll(`iframe.${className}[data-src]`).forEach((iframe) => {
    setIframeBlockedState(iframe, className, !allowed);
  });
};

const hardReloadIframeSrc = (iframe, src) => {
  const replacement = iframe.cloneNode(false);
  replacement.removeAttribute('src');
  replacement.style.display = '';
  delete replacement.dataset.cmpNeedsReload;
  replacement.setAttribute('src', src);
  iframe.replaceWith(replacement);
  return replacement;
};

const isSameUrl = (left, right) => {
  if (!left || !right) {
    return false;
  }

  try {
    return new URL(left, window.location.origin).href === new URL(right, window.location.origin).href;
  } catch (error) {
    return left === right;
  }
};

const hydrateDeferredIframesByClass = (className) => {
  document.querySelectorAll(`iframe.${className}[data-src]`).forEach((iframe) => {
    const deferredSrc = iframe.getAttribute('data-src');
    const needsReload = iframe.dataset.cmpNeedsReload === 'true';
    const hasSrc = iframe.hasAttribute('src') && iframe.getAttribute('src');

    if (deferredSrc && (!hasSrc || needsReload)) {
      if (needsReload) {
        hardReloadIframeSrc(iframe, deferredSrc);
        return;
      }

      iframe.setAttribute('src', deferredSrc);
      delete iframe.dataset.cmpNeedsReload;
    }
  });
};

const hydrateAllowedByFallback = () => {
  const functionalAllowed = shouldAllowFunctionalEmbedsByRegion();
  const socialAllowed = shouldAllowSocialEmbedsByRegion();

  updateBlockedStateForClass(CMP_FUNCTIONAL_CLASS, functionalAllowed);
  updateBlockedStateForClass(CMP_SOCIAL_CLASS, socialAllowed);

  if (functionalAllowed) {
    hydrateDeferredIframesByClass(CMP_FUNCTIONAL_CLASS);
  }

  if (socialAllowed) {
    hydrateDeferredIframesByClass(CMP_SOCIAL_CLASS);
  }
};

const forceRefreshPlaylistEmbeds = (allowed) => {
  document.querySelectorAll(PLAYLIST_EMBED_SELECTOR).forEach((iframe) => {
    if (!iframe.classList.contains(CMP_FUNCTIONAL_CLASS)) {
      return;
    }

    setIframeBlockedState(iframe, CMP_FUNCTIONAL_CLASS, !allowed);

    if (!allowed) {
      return;
    }

    const deferredSrc = iframe.getAttribute('data-src');
    if (deferredSrc) {
      const currentSrc = iframe.getAttribute('src');
      const needsReload = iframe.dataset.cmpNeedsReload === 'true';
      const missingSrc = !currentSrc;
      const srcMismatch = !missingSrc && !isSameUrl(currentSrc, deferredSrc);

      if (needsReload || missingSrc || srcMismatch) {
        hardReloadIframeSrc(iframe, deferredSrc);
      }
    }
  });
};

const hydrateIfAllowedFromCmp = (cmp) => {
  const hydrateFromState = (functionalAllowed, socialAllowed) => {
    if (typeof functionalAllowed === 'boolean') {
      const resolvedFunctionalAllowed = resolveFunctionalAllowed(functionalAllowed);
      functionalAllowedState = resolvedFunctionalAllowed;
      updateBlockedStateForClass(CMP_FUNCTIONAL_CLASS, resolvedFunctionalAllowed);
    }

    if (typeof socialAllowed === 'boolean') {
      const resolvedSocialAllowed = resolveSocialAllowed(socialAllowed);
      socialAllowedState = resolvedSocialAllowed;
      updateBlockedStateForClass(CMP_SOCIAL_CLASS, resolvedSocialAllowed);
    }

    if (functionalAllowedState === true) {
      hydrateDeferredIframesByClass(CMP_FUNCTIONAL_CLASS);
    }

    if (socialAllowedState === true) {
      hydrateDeferredIframesByClass(CMP_SOCIAL_CLASS);
    }
  };

  if (cmp?.ot && typeof cmp.ot.getState === 'function') {
    try {
      cmp.ot.getState((targetingAllowed, performanceAllowed, functionalAllowed, socialAllowed) => {
        hydrateFromState(
          typeof functionalAllowed === 'boolean' ? functionalAllowed : null,
          typeof socialAllowed === 'boolean' ? socialAllowed : null,
        );
      });
      return;
    } catch (error) {
      // Fall back below.
    }
  }

  if (cmp?.ot && typeof cmp.ot.socialAllowed === 'function' && typeof cmp.ot.functionalAllowed === 'function') {
    try {
      let functionalResult = null;
      let socialResult = null;

      functionalResult = cmp.ot.functionalAllowed((allowed) => {
        hydrateFromState(Boolean(allowed), null);
      });

      socialResult = cmp.ot.socialAllowed((allowed) => {
        hydrateFromState(null, Boolean(allowed));
      });

      if (typeof functionalResult === 'boolean' || typeof socialResult === 'boolean') {
        hydrateFromState(Boolean(functionalResult), Boolean(socialResult));
      }
      return;
    } catch (error) {
      // Fall back to storage-derived status below.
    }
  }

  hydrateAllowedByFallback();
};

const refreshEmbedsAfterConsentChange = () => {
  // Reset cached consent booleans so we always re-evaluate latest CMP state.
  socialAllowedState = null;
  functionalAllowedState = null;
  hydrateAllowedByFallback();
  queueIframeLoad();

  // Some CMP regions update state asynchronously after save; retry hydration.
  window.setTimeout(() => {
    const functionalAllowed = shouldAllowFunctionalEmbedsByRegion();
    forceRefreshPlaylistEmbeds(functionalAllowed);
    queueIframeLoad();
  }, 350);

  window.setTimeout(() => {
    const functionalAllowed = shouldAllowFunctionalEmbedsByRegion();
    forceRefreshPlaylistEmbeds(functionalAllowed);
    queueIframeLoad();
  }, 1200);
};

const queueIframeLoad = () => {
  if (!window.cbsoptanon || !Array.isArray(window.cbsoptanon.cmd)) {
    return;
  }

  window.cbsoptanon.cmd.push((cmp) => {
    if (!cmp?.tags) {
      return;
    }

    if (typeof cmp.tags.load === 'function') {
      cmp.tags.load(CMP_IFRAME_TAG);
    }

    // Explicitly hydrate social-gated iframes that rely on data-src.
    if (typeof cmp.tags.loadDeferredTarget === 'function') {
      cmp.tags.loadDeferredTarget(CMP_FUNCTIONAL_CLASS, CMP_IFRAME_TAG, 'src');
    }

    if (typeof cmp.tags.loadDeferredTarget === 'function') {
      cmp.tags.loadDeferredTarget(CMP_SOCIAL_CLASS, CMP_IFRAME_TAG, 'src');
    }

    hydrateIfAllowedFromCmp(cmp);
  });
};

const registerConsentChangeReload = () => {
  if (!window.cbsoptanon || !Array.isArray(window.cbsoptanon.cmd)) {
    return;
  }

  window.cbsoptanon.cmd.push((cmp) => {
    if (typeof cmp?.addOnConsentChangedHandler === 'function') {
      cmp.addOnConsentChangedHandler(() => {
        refreshEmbedsAfterConsentChange();
      });
    }
  });
};

const registerConsentStorageWatcher = () => {
  lastConsentStateSnapshot = getConsentStateSnapshot();

  const checkForConsentChanges = () => {
    const nextSnapshot = getConsentStateSnapshot();
    if (nextSnapshot !== lastConsentStateSnapshot) {
      lastConsentStateSnapshot = nextSnapshot;
      refreshEmbedsAfterConsentChange();
    }
  };

  window.setInterval(checkForConsentChanges, 1000);
  window.addEventListener('storage', checkForConsentChanges);
};

document.addEventListener('DOMContentLoaded', () => {
  registerCmpJurisdictionEvents();
  primeCmpJurisdictionFromStorage();
  hydrateAllowedByFallback();

  queueIframeLoad();
  registerConsentChangeReload();
  registerConsentStorageWatcher();

  // Some integrations emit this as a browser event when consent updates.
  window.addEventListener('ketchPermitChanged', refreshEmbedsAfterConsentChange);
  window.addEventListener('paramount:cmp-load-iframes', queueIframeLoad);
  window.addEventListener(CMP_CONSENT_READY_EVENT, refreshEmbedsAfterConsentChange);
  window.addEventListener(CMP_NETWORK_JURISDICTION_EVENT, refreshEmbedsAfterConsentChange);
});

window.paramountCmpEmbeds = {
  shouldAllowSocialEmbedsByRegion,
  shouldAllowFunctionalEmbedsByRegion,
  queueIframeLoad,
  getBlockedMessage,
};
